import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { getUnixTime } from 'date-fns';
import { batch } from 'react-redux';
import { push } from 'connected-next-router';
import createAction from '@/Framework/State/Redux/createAction';
import isEmptyString from '@/Framework/dataHelpers/string/isEmptyString';
import { addQueryStringParam, removeQueryStringParam } from '@/Framework/url/helpers/queryString';
import TraceRepository from '@/finsight/infrastructure/repository/TraceRepository';
import { mergeCheckedIdsToParent } from '@/Framework/UI/Organisms/Filters/helpers';
import * as actionTypes from './actionTypes';
import isServer from '@/Framework/Router/Next/isServer';
import Logger from '@/Framework/browser/log/Logger';
import { TRACE_STORE_NAME } from '@/finsight/application/actions/trace/constants';
import traceFiltersSelectors from './selectors';
import { getFormatUrl } from '@/finsight/ui/common/helpers';
import { toYearsFormatter } from '@/finsight/application/helpers';

export const applyFilter = (filters, checkboxes) => (dispatch, getState) => {
  const { router, ...state } = getState();
  let initialFilters = traceFiltersSelectors.getInitialFilters(state);
  const newFiltersPayload = dispatch(getFiltersPayload(filters, checkboxes));

  const isInitialFilters = isEqual(initialFilters, filters);
  const appliedFilters = isInitialFilters ? initialFilters : filters;
  dispatch(payloadChange(newFiltersPayload));
  dispatch(appliedFiltersChange(appliedFilters));

  if (isInitialFilters) {
    const newSearch = removeQueryStringParam(router.location, ['dateFrom', 'dateTo', 'hash']);
    dispatch(push(`${ router.location.pathname }?${ newSearch }`));
  }
};

export const fetchFilter = (includedAssetClassId, filters, checkboxes) => async (dispatch, getState) => {
  const { router, ...state } = getState();
  let initialFilters = traceFiltersSelectors.getInitialFilters(state);
  const newFiltersPayload = dispatch(getFiltersPayload(filters, checkboxes));

  let response = await dispatch(getTraceFilter({ includedAssetClassId, ...newFiltersPayload }));
  const traceFilter = response.filter || {};
  const isTraceFilter = !!Object.keys(traceFilter).length;
  const hash = response.hash || '';
  const isInitialFilters = isEqual(initialFilters, filters);
  const isHash = isEmptyString(hash);

  if (isTraceFilter) {
    const { tradeDate } = traceFilter;
    dispatch(setMinMaxDatePeriod(tradeDate.range.min, tradeDate.range.max));
  }
  if (!isHash && !isInitialFilters) {
    const newSearch = addQueryStringParam(router.location, { hash });
    dispatch(push(`${ router.location.pathname }?${ newSearch }`));
  }
};

export const getTraceFilter = (filtersPayload) => async (dispatch, getState) => {
  dispatch(createAction(actionTypes.GET_TRACE_FILTER));
  try {
    const filter = {
      ...filtersPayload,
    };
    const traceRepository = getState().container.get(TraceRepository);
    const response = await traceRepository.getTraceFilter(filter);

    dispatch(createAction(actionTypes.GET_TRACE_FILTER_SUCCESS, response));
    return response;
  } catch (err) {
    dispatch(createAction(actionTypes.GET_TRACE_FILTER_ERROR));
    return undefined;
  }
};

export const payloadChange = (payload) => createAction(actionTypes.TRACE_FILTER_PAYLOAD, payload);

export const initialFiltersChange = (payload) => createAction(actionTypes.TRACE_INITIAL_FILTERS, payload);

export const appliedFiltersChange = (payload) => createAction(actionTypes.TRACE_FILTER_APPLY, payload);

export interface IInitialState {
  dateFrom: number | null,
  dateTo: number | null,
  includedContraPartyTypes: string[],
  coupon: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  includedIssuerIds: string[],
  maturityDate: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  price: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  includedRemunerations: string[],
  includedReportingPartySides: string[],
  includedReportingPartyTypes: string[],
  includedSubsectorIds: string[],
  size: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  tradeDate: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  includedTradeStatuses: string[],
  tradeTime: {
    range: {
      min: number | null,
      max: number | null,
    },
    minValue: number | null,
    maxValue: number | null,
  },
  includedDealIds: string[],
  excludedSectorIds: string[],
}
export const getInitialState = (): IInitialState => ({
  dateFrom: null,
  dateTo: null,
  includedContraPartyTypes: [],
  coupon: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  includedIssuerIds: [],
  maturityDate: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  price: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  includedRemunerations: [],
  includedReportingPartySides: [],
  includedReportingPartyTypes: [],
  includedSubsectorIds: [],
  size: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  tradeDate: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  includedTradeStatuses: [],
  tradeTime: {
    range: {
      min: null,
      max: null,
    },
    minValue: null,
    maxValue: null,
  },
  includedDealIds: [],
  excludedSectorIds: [],
});

export const getFiltersPayload = (filters = {}, checkboxes) => (dispatch, getState) => {
  const traceFilter = traceFiltersSelectors.getItemsFilter(getState());
  const isTraceFilter = !!Object.keys(traceFilter).length;
  let { ...newFilters } = cloneDeep(filters) as any;

  const filtersRanges = {
    coupon: newFilters.coupon,
    maturityDate: newFilters.maturityDate,
    price: newFilters.price,
    size: newFilters.size,
    tradeDate: newFilters.tradeDate,
    tradeTime: newFilters.tradeTime,
  };

  // If range filter value is equal to initial state,
  // we set it to 'null'
  if (isTraceFilter) {
    const traceFilterRanges = mapRangesForFilters(traceFilter);
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (let key in filtersRanges) {
      if (isEqual(filtersRanges[key].range.min, traceFilterRanges[key].range.min)) {
        newFilters[key].range.min = null;
      }
      if (isEqual(filtersRanges[key].range.max, traceFilterRanges[key].range.max)) {
        newFilters[key].range.max = null;
      }
    }
  }

  let rangesPayload = mapRangesForPayload(newFilters);
  // Cleanup
  Object.keys(filtersRanges).forEach((key) => delete newFilters[key]);

  const sectorIds = mergeCheckedIdsToParent(
    checkboxes,
    newFilters,
    'sectorList',
    'includedSubsectorIds',
  );

  return {
    ...newFilters,
    includedSectorIds: sectorIds.parentIds,
    includedSubsectorIds: sectorIds.childrenIds,
    ...rangesPayload,
  };
};

export const mapRangesForFilters = (traceFilter) => ({
  coupon: {
    range: traceFilter.coupon.range,
    minValue: traceFilter.coupon.range.min,
    maxValue: traceFilter.coupon.range.max,
  },
  maturityDate: {
    range: {
      min: toYearsFormatter(traceFilter.maturityDate.range.min),
      max: toYearsFormatter(traceFilter.maturityDate.range.max),
    },
    minValue: toYearsFormatter(traceFilter.maturityDate.range.min),
    maxValue: toYearsFormatter(traceFilter.maturityDate.range.max),
  },
  price: {
    range: traceFilter.price.range,
    minValue: traceFilter.price.range.min,
    maxValue: traceFilter.price.range.max,
  },
  size: {
    range: traceFilter.size.range,
    minValue: traceFilter.size.range.min,
    maxValue: traceFilter.size.range.max,
  },
  tradeDate: {
    range: traceFilter.tradeDate.range,
    minValue: traceFilter.tradeDate.range.min,
    maxValue: traceFilter.tradeDate.range.max,
  },
  tradeTime: {
    range: traceFilter.tradeTime.range,
    minValue: traceFilter.tradeTime.range.min,
    maxValue: traceFilter.tradeTime.range.max,
  },
});

export const mapRangesForPayload = (filters) => ({
  includedCouponFrom: filters.coupon.range.min,
  includedCouponTo: filters.coupon.range.max,

  maturityDateFrom: getUnixTime(new Date(filters.maturityDate.range.min?.toString())) || null,
  maturityDateTo: getUnixTime(new Date(filters.maturityDate.range.max?.toString())) || null,

  includedPriceFrom: filters.price.range.min,
  includedPriceTo: filters.price.range.max,

  includedSizeFrom: filters.size.range.min,
  includedSizeTo: filters.size.range.max,

  includedTimeFrom: filters.tradeTime.range.min,
  includedTimeTo: filters.tradeTime.range.max,

  dateFrom: filters.dateFrom,
  dateTo: filters.dateTo,
});

export const mapRangesPayloadToFilters = (payload, traceFilter = {}) => {
  const rangesForFilters = mapRangesForFilters(traceFilter);
  return {
    coupon: {
      range: {
        min: payload.includedCouponFrom || rangesForFilters.coupon.range.min,
        max: payload.includedCouponTo || rangesForFilters.coupon.range.max,
      },
      minValue: rangesForFilters.coupon.range.min,
      maxValue: rangesForFilters.coupon.range.max,
    },

    maturityDate: {
      range: {
        min: toYearsFormatter(payload.maturityDateFrom || rangesForFilters.maturityDate.range.min),
        max: toYearsFormatter(payload.maturityDateTo || rangesForFilters.maturityDate.range.max),
      },
      minValue: toYearsFormatter(rangesForFilters.maturityDate.range.min),
      maxValue: toYearsFormatter(rangesForFilters.maturityDate.range.max),
    },

    price: {
      range: {
        min: payload.includedPriceFrom || rangesForFilters.price.range.min,
        max: payload.includedPriceTo || rangesForFilters.price.range.max,
      },
      minValue: rangesForFilters.price.range.min,
      maxValue: rangesForFilters.price.range.max,
    },

    size: {
      range: {
        min: payload.includedSizeFrom || rangesForFilters.size.range.min,
        max: payload.includedSizeTo || rangesForFilters.size.range.max,
      },
      minValue: rangesForFilters.size.range.min,
      maxValue: rangesForFilters.size.range.max,
    },

    tradeDate: {
      range: {
        min: payload.dateFrom || rangesForFilters.tradeDate.range.min,
        max: payload.dateTo || rangesForFilters.tradeDate.range.max,
      },
      minValue: rangesForFilters.tradeDate.range.min,
      maxValue: rangesForFilters.tradeDate.range.max,
    },

    tradeTime: {
      range: {
        min: payload.includedTimeFrom || rangesForFilters.tradeTime.range.min,
        max: payload.includedTimeTo || rangesForFilters.tradeTime.range.max,
      },
      minValue: rangesForFilters.tradeTime.range.min,
      maxValue: rangesForFilters.tradeTime.range.max,
    },
    issuerList: payload.includedIssuerIds,
  };
};

export const mapHashPayloadToFilters = (payload, traceFilter = { sectorList: [] }) => {
  const isTraceFilter = !!Object.keys(traceFilter).length;
  let filters = { ...payload };
  let includedSubsectorIds = [...filters.includedSubsectorIds];
  let currentFaceRangeIds = [];

  if (isTraceFilter) {
    if (filters.includedSectorIds && filters.includedSectorIds.length) {
      filters.includedSectorIds.forEach((sectorId) => {
        let { subsectorList } = traceFilter.sectorList.find((sector) => sector.sectorId === sectorId);
        subsectorList.forEach((subSector) => {
          includedSubsectorIds.push(subSector.subsectorId);
        });
      });
    }
  }

  const filtersToCleanupKeys = [
    'contraPartyTypeList',
    'issuerList',
    'maturityDate',
    'price',
    'remunerationList',
    'reportingPartySideList',
    'reportingPartyTypeList',
    'sectorList',
    'size',
    'tradeDate',
    'tradeStatusList',
    'tradeTime',
  ];
  // Cleanup
  filtersToCleanupKeys.forEach((key) => delete filters[key]);

  return {
    ...filters,
    includedSubsectorIds,
    currentFaceRangeIds,
    ...mapRangesPayloadToFilters(payload, traceFilter),
  };
};

export const setMinMaxDatePeriod = (dateFrom: number, dateTo: number) => (dispatch) => {
  batch(() => {
    dispatch(createAction(actionTypes.TRACE_FILTER_MIN_DATE_FROM, dateFrom));
    dispatch(createAction(actionTypes.TRACE_FILTER_MAX_DATE_TO, dateTo));
  });
};

export const initFilters = (
  filtersPayload,
  filtersState = getInitialState(),
  isHashPayload: boolean = false,
  includedAssetClassId: string = null,
  filtersUpdate,
) => async (dispatch) => {
  try {
    let response = await dispatch(getTraceFilter({ includedAssetClassId, ...filtersPayload }));
    const traceFilter = response.filter;

    filtersState = Object.assign(filtersState, mapRangesForFilters(traceFilter));

    const initialFilters = Object.assign(getInitialState(), mapRangesForFilters(traceFilter));
    dispatch(initialFiltersChange(initialFilters));

    if (isHashPayload) {
      const filters = mapHashPayloadToFilters(filtersPayload, traceFilter);

      batch(() => {
        filtersUpdate(TRACE_STORE_NAME, filters);
        dispatch(appliedFiltersChange(filters));
      });
    } else {
      batch(() => {
        filtersUpdate(TRACE_STORE_NAME, filtersState);
        dispatch(appliedFiltersChange(filtersState));
      });
    }

    dispatch(setMinMaxDatePeriod(traceFilter.tradeDate.range.min, traceFilter.tradeDate.range.max));
  } catch (err) {
    Logger.warn(err);
  }
};

export const resetFilters = (includedAssetClassId: string, filtersUpdate, checkboxes) => async (dispatch, getState) => {
  const { location } = getState().router;
  const initialState = getInitialState();
  // Pass filters through all components
  let newFiltersPayload = dispatch(getFiltersPayload(getInitialState(), checkboxes));

  dispatch(payloadChange(newFiltersPayload));
  await dispatch(initFilters(newFiltersPayload, initialState, false, includedAssetClassId, filtersUpdate));

  const newSearch = removeQueryStringParam(location, ['dateFrom', 'dateTo', 'hash']);
  const pathname = isServer() ? location.pathname : window.location.pathname;
  dispatch(push(getFormatUrl(`${ pathname }?${ newSearch }`)));
};
